/**
 * 支持的语言类型
 */
type SupportedLocale =
  | "zh-CN"
  | "zh-TW"
  | "en-US"
  | "ja-JP"
  | "ko-KR"
  | "es-ES"
  | "fr-FR"
  | "de-DE"
  | "ru-RU";

/**
 * 地区信息接口
 */
interface LocaleInfo {
  /** 语言代码 */
  locale: SupportedLocale;
  /** 语言名称 */
  name: string;
  /** 本地化名称 */
  nativeName: string;
  /** 地区代码 */
  region: string;
  /** 时区 */
  timezone?: string;
  /** 货币符号 */
  currency?: string;
  /** 日期格式 */
  dateFormat: string;
  /** 时间格式 */
  timeFormat: string;
  /** 是否为RTL */
  isRTL: boolean;
}

/**
 * 支持的地区配置
 */
const LOCALE_CONFIG: Record<SupportedLocale, LocaleInfo> = {
  "zh-CN": {
    locale: "zh-CN",
    name: "Chinese (Simplified)",
    nativeName: "简体中文",
    region: "CN",
    timezone: "Asia/Shanghai",
    currency: "¥",
    dateFormat: "YYYY年MM月DD日",
    timeFormat: "HH:mm:ss",
    isRTL: false,
  },
  "zh-TW": {
    locale: "zh-TW",
    name: "Chinese (Traditional)",
    nativeName: "繁體中文",
    region: "TW",
    timezone: "Asia/Taipei",
    currency: "NT$",
    dateFormat: "YYYY年MM月DD日",
    timeFormat: "HH:mm:ss",
    isRTL: false,
  },
  "en-US": {
    locale: "en-US",
    name: "English (US)",
    nativeName: "English",
    region: "US",
    timezone: "America/New_York",
    currency: "$",
    dateFormat: "MM/DD/YYYY",
    timeFormat: "hh:mm:ss A",
    isRTL: false,
  },
  "ja-JP": {
    locale: "ja-JP",
    name: "Japanese",
    nativeName: "日本語",
    region: "JP",
    timezone: "Asia/Tokyo",
    currency: "¥",
    dateFormat: "YYYY年MM月DD日",
    timeFormat: "HH:mm:ss",
    isRTL: false,
  },
  "ko-KR": {
    locale: "ko-KR",
    name: "Korean",
    nativeName: "한국어",
    region: "KR",
    timezone: "Asia/Seoul",
    currency: "₩",
    dateFormat: "YYYY년 MM월 DD일",
    timeFormat: "HH:mm:ss",
    isRTL: false,
  },
  "es-ES": {
    locale: "es-ES",
    name: "Spanish",
    nativeName: "Español",
    region: "ES",
    timezone: "Europe/Madrid",
    currency: "€",
    dateFormat: "DD/MM/YYYY",
    timeFormat: "HH:mm:ss",
    isRTL: false,
  },
  "fr-FR": {
    locale: "fr-FR",
    name: "French",
    nativeName: "Français",
    region: "FR",
    timezone: "Europe/Paris",
    currency: "€",
    dateFormat: "DD/MM/YYYY",
    timeFormat: "HH:mm:ss",
    isRTL: false,
  },
  "de-DE": {
    locale: "de-DE",
    name: "German",
    nativeName: "Deutsch",
    region: "DE",
    timezone: "Europe/Berlin",
    currency: "€",
    dateFormat: "DD.MM.YYYY",
    timeFormat: "HH:mm:ss",
    isRTL: false,
  },
  "ru-RU": {
    locale: "ru-RU",
    name: "Russian",
    nativeName: "Русский",
    region: "RU",
    timezone: "Europe/Moscow",
    currency: "₽",
    dateFormat: "DD.MM.YYYY",
    timeFormat: "HH:mm:ss",
    isRTL: false,
  },
};

/**
 * 默认语言设置
 */
const DEFAULT_LOCALE: SupportedLocale = "en-US";

/**
 * 获取浏览器语言列表
 * @returns 浏览器支持的语言列表
 */
const getBrowserLanguages = (): string[] => {
  if (typeof window === "undefined") {
    return [DEFAULT_LOCALE];
  }

  const languages: string[] = [];

  // 获取用户首选语言
  if (navigator.language) {
    languages.push(navigator.language);
  }

  // 获取用户语言列表
  if (navigator.languages) {
    languages.push(...navigator.languages);
  }

  // 兼容旧版浏览器
  if ((navigator as any).userLanguage) {
    languages.push((navigator as any).userLanguage);
  }

  if ((navigator as any).browserLanguage) {
    languages.push((navigator as any).browserLanguage);
  }

  if ((navigator as any).systemLanguage) {
    languages.push((navigator as any).systemLanguage);
  }

  return Array.from(new Set(languages)).filter(Boolean);
};

/**
 * 标准化语言代码
 * @param language 原始语言代码
 * @returns 标准化的语言代码
 */
const normalizeLanguage = (language: string): SupportedLocale | null => {
  if (!language) {
    return null;
  }

  const lang = language.toLowerCase().replace("_", "-");
  const supportedLocales = Object.keys(LOCALE_CONFIG) as SupportedLocale[];

  // 精确匹配
  const exactMatch = supportedLocales.find(
    (locale) => locale.toLowerCase() === lang,
  );
  if (exactMatch) {
    return exactMatch;
  }

  // 语言代码匹配（忽略地区）
  const langCode = lang.split("-")[0];
  const languageMatch = supportedLocales.find(
    (locale) => locale.toLowerCase().split("-")[0] === langCode,
  );
  if (languageMatch) {
    return languageMatch;
  }

  return null;
};

/**
 * 检测用户首选语言
 * @param fallback 备用语言
 * @returns 检测到的语言信息
 */
const detectUserLocale = (
  fallback: SupportedLocale = DEFAULT_LOCALE,
): LocaleInfo => {
  const browserLanguages = getBrowserLanguages();

  for (const language of browserLanguages) {
    const normalizedLang = normalizeLanguage(language);
    if (normalizedLang && LOCALE_CONFIG[normalizedLang]) {
      return LOCALE_CONFIG[normalizedLang];
    }
  }

  return LOCALE_CONFIG[fallback];
};

/**
 * 根据地区获取时区信息
 * @param locale 语言代码
 * @returns 时区信息
 */
const getTimezone = (locale: SupportedLocale): string => {
  return LOCALE_CONFIG[locale]?.timezone || "UTC";
};

export const isKoreanUser = () => {
  const userLocale = detectUserLocale();
  const userTimezone = getTimezone(userLocale.locale);

  // 方法1: 通过语言代码判断
  const isKoreanByLocale = userLocale.locale === "ko-KR";

  // 方法2: 通过时区判断韩国地区
  const koreanTimezones = ["Asia/Seoul"];
  const isKoreanByTimezone = koreanTimezones.includes(userTimezone);

  // 方法3: 通过浏览器时区判断
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isKoreanByBrowserTimezone = koreanTimezones.includes(browserTimezone);

  const result =
    isKoreanByLocale || isKoreanByTimezone || isKoreanByBrowserTimezone;

  return result;
};
